import * as React from 'react';
import type { AgencyReportUpload } from '../../MedicareProAPI/Entities/Agency/AgencyReportUpload';
import type { GlobCompanies } from '../../MedicareProAPI/Entities/Glob/GlobCompanies';
import { AgencyReportUploadRepository } from '../../MedicareProAPI/Repositories/Agency/AgencyReportUploadRepository';
import { AgencyAgentContractRepository } from '../../MedicareProAPI/Repositories/Agency/AgencyAgentContractRepository';
import { GlobCompaniesRepository } from '../../MedicareProAPI/Repositories/Glob/GlobCompaniesRepository';
import { WebAPI } from '../../MedicareProAPI/API/WebAPI';

type ExtractionResponse = {
  processed: number;
  next_offset: number;
  complete: boolean;
};

export interface ContractCreationImportProps {
  props: any;
  agencyReportUpload: AgencyReportUpload;
  onFileCreate: (agencyReportUpload: AgencyReportUpload) => void;
  handleGoBack: () => void;
  handleProcessedFile: (agencyReportUpload: AgencyReportUpload) => void;
  handleOnFileUpdate: (reportId: number, payload: object) => void;
}

/**
 * Component for Agency upload of an contract creation template file.
 *
 * @author Andrew Brown
 */

export const ContractCreationImport = (props: ContractCreationImportProps): React.JSX.Element => {
  const globCompaniesRepository = new GlobCompaniesRepository();
  globCompaniesRepository.setAccessToken(props.props.userAccessToken);

  const agencyReportUploadRepository = new AgencyReportUploadRepository();
  agencyReportUploadRepository.setAccessToken(props.props.userAccessToken);

  const agencyAgentContractRepository = new AgencyAgentContractRepository();
  agencyAgentContractRepository.setAccessToken(props.props.userAccessToken);

  const webAPI = new WebAPI();
  webAPI.setAccessToken(props.props.userAccessToken);

  const [fileCompanySelected, setFileCompanySelected] = React.useState<GlobCompanies>();
  const [rtsYearSelected, setRtsYearSelected] = React.useState<string>();
  const [file, setFile] = React.useState<any>();
  const [fileSelected, setFileSelected] = React.useState<boolean>(false);
  const [fileTypeIsValid, setFileTypeIsValid] = React.useState<boolean>(true);
  const [fileIsBeingProcessed, setFileIsBeingProcessed] = React.useState<boolean>(false);
  const [extractingRows, setExtractingRows] = React.useState<boolean>(false);
  const [extractingComplete, setExtractingComplete] = React.useState<boolean>(false);
  const [processedCount, setProcessedCount] = React.useState<number>(0);
  const [checkingValidity, setCheckingValidity] = React.useState<boolean>(false);
  const [fileIsValid, setFileIsValid] = React.useState<boolean>(true);

  const [fileUploadError, setFileUploadError] = React.useState<string>();

  const [agencyReportUpload, setAgencyReportUpload] = React.useState<AgencyReportUpload>();

  const [globCompanies, setGlobCompanies] = React.useState<Array<GlobCompanies>>();

  const [isProcessing, setIsProcessing] = React.useState<boolean>(false);

  const [processingContracts, setProcessingContracts] = React.useState<boolean>(false);

  const date = new Date();
  const thisYear = date.getFullYear();
  const nextYear = thisYear + 1;

  const years = [thisYear, nextYear];

  /**
   * Handles setting the company selected in RTS form
   * @param {React.ChangeEvent<HTMLSelectElement>} event
   */
  const handleFileCompanySelected = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const index = event.target.value;
    setFileCompanySelected(globCompanies[index]);
  };

  /**
   * Handles setting the file and validating the type
   * @param {React.ChangeEvent<HTMLSelectElement>} event
   */
  const handleFileSelected = (event: React.ChangeEvent<HTMLInputElement>) => {
    const permittedFileTypes = [
      'text/csv',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
    ];
    const file = event.target.files[0];

    if (!permittedFileTypes.includes(file.type)) {
      setFileTypeIsValid(false);
      return;
    } else {
      setFileTypeIsValid(true);
      setFileSelected(true);
      setFile(file);
    }
  };

  /**
   * Handles uploading the file and saving the AgencyReportUpload
   */
  const handleFileUpload = () => {
    setFileIsBeingProcessed(true);
    setCheckingValidity(true);

    const formData = new FormData();

    formData.append('file', file);
    formData.append('companyId', fileCompanySelected.getCompanyId());
    formData.append('uploadType', 'Contract Creation');

    agencyReportUploadRepository
      .createAgencyReportUpload(props.props.userId, props.props.agencyId, formData)
      .then((response) => {
        setFileIsValid(true);
        setCheckingValidity(false);
        setAgencyReportUpload(response);
        props.onFileCreate(response);
        extractChunks(response.getId(), 300, 100);
        setExtractingRows(true);
      })
      .catch((error) => {
        setFileIsValid(false);
        setFileUploadError(error.message);
        setCheckingValidity(false);
      });
  };

  /**
   * Handles clicking go back
   */
  const handleGoBack = () => {
    setFileIsBeingProcessed(false);
    setCheckingValidity(false);
    setFileCompanySelected(undefined);
    setRtsYearSelected(undefined);
    setFileSelected(undefined);
    setFileUploadError(undefined);

    props.handleGoBack();
  };

  /**
   * OLDER VERSION THAT PROCESSES ALL CONTRACTS IN ONE GO.
   * Handles processing the saved file for contract creation operations
   * @param {number} id
   */
  const handleProcessContracts = (id) => {
    /*  setProcessingContracts(true);
        agencyAgentContractRepository.processAgencyAgentContractFromUpload(props.props.userId, props.props.agencyId,id ).then(response => {
            props.handleOnFileUpdate(agencyReportUpload.getId(), {contractsProcessed : true});
            setProcessingContracts(false);
          })  
        */
  };

  const handleDownloadRowsResults = async (id) => {
    try {
      const exportUrl = await webAPI.downloadAgencyReportUploadResults(
        props.props.userId,
        props.props.agencyId,
        id
      );

      if (typeof exportUrl !== 'string' || !exportUrl) {
        throw new Error('Invalid export URL received');
      }

      const [baseUrl, queryString] = exportUrl.split('?');

      const form = document.createElement('form');
      form.method = 'get';
      form.action = baseUrl;

      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'accessToken';
      input.value = props.props.userAccessToken;
      form.appendChild(input);

      document.body.appendChild(form);
      form.submit();

      document.body.removeChild(form);
    } catch (error) {
      console.error('Error downloading missing contracts:', error);
    }
  };

  const processContractsInChunks = (
    uploadId: number,
    delayMs = 300,
    maxAttempts = 100,
    chunkSize = 200
  ) => {
    let attemptCount = 0;

    const runProcessing = async () => {
      try {
        const result = await agencyAgentContractRepository.processAgencyAgentContractChunk(
          props.props.userId,
          props.props.agencyId,
          uploadId
        );

        console.log(`Processed ${result.processed} contracts, next offset: ${result.next_offset}`);
        setProcessedCount((prev) => prev + result.processed);
        if (result.complete) {
          console.log('Contract processing complete');
          props.handleOnFileUpdate(uploadId, { contractsProcessed: true });
          setProcessingContracts(false);
          return;
        }

        attemptCount++;
        if (attemptCount >= maxAttempts) {
          throw new Error('Max attempts reached — aborting contract processing.');
        }

        setTimeout(runProcessing, delayMs);
      } catch (err) {
        console.error('Contract processing error:', err);
        setProcessingContracts(false);
        // Optionally notify user of failure
      }
    };

    setProcessingContracts(true);
    runProcessing();
  };

  const extractChunks = (uploadId, delayMs = 300, maxAttempts = 100) => {
    let attemptCount = 0;
    const runExtraction = async () => {
      try {
        const result: ExtractionResponse =
          await agencyReportUploadRepository.extractAgencyAgentUpload(
            props.props.userId,
            props.props.agencyId,
            uploadId
          );

        console.log(`Processed ${result.processed}, next offset: ${result.next_offset}`);

        if (result.complete) {
          console.log('Extraction complete');
          setExtractingRows(false);
          setExtractingComplete(true);
          // onComplete?.();
          return;
        }

        attemptCount++;
        if (attemptCount >= maxAttempts) {
          throw new Error('Max attempts reached — aborting extraction.');
        }

        setTimeout(runExtraction, delayMs);
      } catch (err) {
        console.error('Extraction error:', err);
        // onError?.(err);
      }
    };

    runExtraction();
  };

  /**
   * Handles getting GlobCompanies to select and setting the AgencyReportUpload if one was passed by parent
   */
  React.useEffect(() => {
    if (globCompanies === undefined) {
      globCompaniesRepository.getGlobCompanies().then((response) => {
        setGlobCompanies(response.getItems());
      });
    }

    if (props.agencyReportUpload) {
      setAgencyReportUpload(props.agencyReportUpload);
      setFileIsBeingProcessed(true);
      setFileTypeIsValid(true);
      setFileSelected(true);
      setCheckingValidity(false);
      setFileIsValid(true);
      setExtractingComplete(props.agencyReportUpload.getExtractionStatus() === 'complete');
      setProcessedCount(0);
      setExtractingRows(false);
    }
  }, [props.agencyReportUpload]);

  return (
    <div className="mt-4">
      <div className="alert alert-info mb-2 mb-3" style={{ fontSize: '10pt' }}>
        <i className="far fa-comment mr-2"></i>
        Use the Contract Creation upload to bulk-create agent contracts for the company selected
        below.
      </div>

      {!fileIsBeingProcessed && (
        <>
          <div>
            <div className="mb-1">Select company</div>
            <select className="form-control form-control-sm" onChange={handleFileCompanySelected}>
              <option>Select...</option>
              {globCompanies !== undefined &&
                globCompanies.map((item, itemIndex) => {
                  return <option value={itemIndex}>{item.getCompanyName()}</option>;
                })}
            </select>
          </div>

          {fileCompanySelected !== undefined && (
            <div className="mt-4">
              <div className="mb-1">Select your file</div>
              <input
                type="file"
                className="form-control form-control-sm"
                onChange={handleFileSelected}
              />
              <br />
              {!fileTypeIsValid && (
                <span className="text-danger">
                  That type of file is not supported. Please use a .csv, .xslx, or .xsl file
                </span>
              )}
            </div>
          )}

          {fileCompanySelected !== undefined &&
            fileSelected &&
            !fileIsBeingProcessed &&
            fileTypeIsValid && (
              <div className="btn btn-primary mt-4" onClick={handleFileUpload}>
                Upload
              </div>
            )}
        </>
      )}
      {fileSelected && fileTypeIsValid && fileIsBeingProcessed && (
        <>
          <div className="p-3 border border-secondary rounded">
            <h5 className="font-weight-bold">
              <i className="far fa-file-alt mr-2 text-primary"></i>
              {file ? file.name : agencyReportUpload.getFileName()}
            </h5>
            <p>
              {checkingValidity && (
                <>
                  <div>
                    <i className="far fa-spinner fa-spin mr-1"></i>
                    <span>Validating file...</span>
                  </div>
                </>
              )}
              {!checkingValidity &&
                fileIsValid &&
                agencyReportUpload.getContractsStatus() !== 'complete' && (
                  <div className="mt-2">
                    <i className="far fa-check-circle text-success mr-2"></i>File is valid
                  </div>
                )}
              {!checkingValidity && !fileIsValid && (
                <>
                  <i className="far fa-exclamation-circle text-danger mr-2"></i>{' '}
                  <span>
                    There are some problems with your file: <b>{fileUploadError}</b>
                  </span>
                </>
              )}
              {extractingRows && (
                <>
                  <div>
                    <i className="far fa-spinner fa-spin mr-1"></i>
                    <span>Extracting rows...</span>
                  </div>
                </>
              )}
              {extractingComplete && agencyReportUpload.getContractsStatus() !== 'complete' && (
                <div className="mt-2">
                  <i className="far fa-check-circle text-success mr-2"></i>Rows extracted
                </div>
              )}

              {!checkingValidity &&
                fileIsValid &&
                agencyReportUpload !== undefined &&
                extractingComplete && (
                  <>
                    <div className="rounded border border-info mt-3 p-2">
                      <div className="font-weight-bold">Add Contracts</div>
                      <div className="rounded bg-light p-2 mt-2">
                        <div>This function will add contracts for each valid NPN.</div>

                        {agencyReportUpload.getContractsStatus() !== 'complete' && (
                          <div
                            className="btn btn-sm btn-primary mt-2"
                            onClick={(e) => processContractsInChunks(agencyReportUpload.getId())}
                          >
                            {processingContracts && (
                              <>
                                <i className="far fa-spinner fa-spin"></i> Processing...
                              </>
                            )}
                            {!processingContracts && !agencyReportUpload.getContractsStatus() && (
                              <>Process</>
                            )}
                            {!processingContracts &&
                              agencyReportUpload.getContractsStatus() === 'processing' && (
                                <>Continue</>
                              )}
                          </div>
                        )}

                        <div className="mt-2">
                          {agencyReportUpload.getContractsStatus() == 'complete' && (
                            <i className="far fa-check-circle text-success mr-2"></i>
                          )}
                          <b>{processedCount || agencyReportUpload.getContractsOffset()}</b> of{' '}
                          <b>{agencyReportUpload.getFileRowsCount()}</b> processed
                        </div>
                        {agencyReportUpload.getContractsStatus() === 'complete' && (
                          <div
                            className="mt-2 p-1 badge border border-info text-info"
                            onClick={(e) => handleDownloadRowsResults(agencyReportUpload.getId())}
                          >
                            <i className="far fa-download mr-2"></i>Download Results
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                )}
            </p>
          </div>
          <div className="btn border border-secondary mt-3" onClick={handleGoBack}>
            <i className="far fa-chevron-left"></i> Back
          </div>
        </>
      )}
    </div>
  );
};
